import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    Signal,
    signal,
    viewChild,
    WritableSignal
} from "@angular/core";
import {navParam} from "../../navigation/navigation-params";
import {Page} from "../../navigation/page";
import {WalletProvider} from '../../payment/payment';
import {PaymentModel} from '../../payment/payment.model';
import {PhoneNumber} from '../../utils/phone-number';
import {FormComponent} from '../../inputs/form/form.component';
import {PhoneNumberFieldComponent} from '../../inputs/phone-number-field/phone-number-field.component';
import {ContentMessageComponent} from "../../notification/message/content-message.component";
import {MessageLevel} from "../../notification/message/message";
import {OrderingContextModel} from '../../ordering-context/ordering-context.model';
import {ModalPageLayoutComponent} from '../../page-layout/modal-page-layout/modal-page-layout.component';
import {FormsModule} from '@angular/forms';
import {SmsPhoneNumberDirective} from '../../inputs/phone-number-field/sms-phone-number.directive';
import {ButtonComponent} from '../../buttons/button/button.component';
import {PageFooterControlDirective} from '../../page-layout/page-footer-control.directive';
import {ConfigurationModel} from '../../configuration/configuration.model';
import {TranslateModule} from '@ngx-translate/core';

@Component({
    selector: "woa-phone-edit-page",
    templateUrl: "./phone-edit-page.component.html",
    providers: [{provide: Page, useExisting: PhoneEditPageComponent}],
    imports: [
        ButtonComponent,
        ContentMessageComponent,
        FormComponent,
        FormsModule,
        TranslateModule,
        ModalPageLayoutComponent,
        PageFooterControlDirective,
        PhoneNumberFieldComponent,
        SmsPhoneNumberDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PhoneEditPageComponent extends Page {
    private paymentModel: PaymentModel = inject(PaymentModel);
    public orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);

    MessageLevel = MessageLevel;

    public readonly form: Signal<FormComponent> = viewChild.required(FormComponent);

    private readonly mobilePhone: Signal<string> = navParam.required<string>("mobilePhone");
    private readonly confirmationRequired: Signal<boolean> = navParam<boolean>("confirmationRequired", false);

    public readonly suppliedMobilePhone: WritableSignal<string> = signal<string>(this.mobilePhone());

    public readonly confirmedMobilePhone: WritableSignal<string> = signal((() => {
        if (this.confirmationRequired()) {
            return undefined;
        }
        const phoneNumber: PhoneNumber | null = this.configurationModel.parsePhoneNumber()(this.mobilePhone());
        return this.configurationModel.isValidMobileNumberForSms()(phoneNumber?.internationalNumber) ? phoneNumber.internationalNumber : undefined;
    })());

    public readonly error: Signal<string> = computed<string>(() => {
        const walletProvider: WalletProvider = this.paymentModel.walletPaymentProcessor()?.walletProvider();
        return this.confirmationRequired() ? "woa.message.phone.confirmationRequired"
            : !this.suppliedMobilePhone() ? (walletProvider === WalletProvider.APPLE_PAY ? "woa.message.phone.missing.applyPay"
                    : walletProvider === WalletProvider.GOOGLE_PAY ? "woa.message.phone.missing.googlePay"
                        : "woa.message.phone.missing.non-integrated")
                : this.configurationModel.isValidMobileNumberForSms()(this.suppliedMobilePhone()) ? (walletProvider === WalletProvider.APPLE_PAY ? "woa.message.phone.invalid.applyPay"
                        : walletProvider === WalletProvider.GOOGLE_PAY ? "woa.message.phone.invalid.googlePay"
                            : "woa.message.phone.invalid.non-integrated")
                    : (walletProvider === WalletProvider.APPLE_PAY ? "woa.message.phone.non-mobile.applyPay"
                        : walletProvider === WalletProvider.GOOGLE_PAY ? "woa.message.phone.non-mobile.googlePay"
                            : "woa.message.phone.non-mobile.non-integrated");
    });

    public readonly instructions: Signal<string> = computed<string>(() =>
        this.confirmationRequired() ? "woa.message.phone.instructions.confirmationRequired"
            : !this.suppliedMobilePhone() ? "woa.message.phone.instructions.missing"
                : this.configurationModel.isValidMobileNumberForSms()(this.suppliedMobilePhone()) ? "woa.message.phone.instructions.invalid"
                    : "woa.message.phone.instructions.non-mobile");

    confirmPhone(): void {
        if (!this.form().valid) {
            return;
        }

        void this.dismiss({
            confirmed: true,
            mobilePhone: this.confirmedMobilePhone()
        });
    }
}
